/**
 * Light Bar Builder
 * Custom configuration tool for SpeedTech Lights
 */

class LightBarBuilder {
  constructor() {
    this.state = {
      isOpen: false,
      productId: null,
      variantId: null,
      productTitle: '',
      basePrice: 0,
      configKey: null,
      config: null,
      currentStep: 1,
      currentScreen: 'configure', // 'configure', 'accessories' (includes brackets), 'review'
      selectedModule: null,
      moduleColors: {}, // { moduleId: colorKey }
      accessories: [], // [{ id, variantId, title, price }]
      brackets: [], // [{ id, variantId, title, price }]
      quantity: 1,
      specialInstructions: ''
    };

    this.configs = null;
    this.elements = {};
    this.init();
  }

  /**
   * Convert white/clear colors to off-white for better visibility on module fills
   * This does NOT affect swatches - only the module fill colors
   */
  getModuleFillColor(color) {
    if (!color) return '#F2F2F2';
    const upperColor = color.toUpperCase();
    // Convert white/clear to off-white for better visibility
    if (upperColor === '#FFFFFF' || upperColor === '#FFF' || upperColor === 'WHITE') {
      return '#F2F2F2';
    }
    return color;
  }

  async init() {
    await this.loadConfigs();
    this.cacheElements();
    this.bindEvents();
  }

  async loadConfigs() {
    try {
      // Get config URL from data attribute (set by Liquid asset_url filter)
      const builderEl = document.querySelector('#light-bar-builder');
      const configUrl = builderEl?.dataset?.configUrl;

      if (!configUrl) {
        console.error('Config URL not found on builder element');
        return;
      }

      const response = await fetch(configUrl);
      this.configs = await response.json();
    } catch (error) {
      console.error('Failed to load light bar configs:', error);
    }
  }

  cacheElements() {
    this.elements = {
      builder: document.querySelector('.lb-builder'),
      container: document.querySelector('.lb-builder__container'),
      main: document.querySelector('.lb-builder__main'),
      sidebar: document.querySelector('.lb-builder__sidebar'),
      title: document.querySelector('.lb-builder__title'),
      price: document.querySelector('.lb-builder__price'),
      diagram: document.querySelector('.lb-builder__diagram'),
      note: document.querySelector('.lb-builder__note'),
      legend: document.querySelector('.lb-builder__legend'),
      sidebarContent: document.querySelector('.lb-sidebar__content'),
      closeBtn: document.querySelector('.lb-sidebar__close'),
      nextBtn: document.querySelector('.lb-button--primary'),
      screens: {
        configure: document.querySelector('[data-screen="configure"]'),
        accessories: document.querySelector('[data-screen="accessories"]'),
        review: document.querySelector('[data-screen="review"]')
      }
    };
  }

  bindEvents() {
    // Close button
    document.addEventListener('click', (e) => {
      if (e.target.closest('.lb-sidebar__close')) {
        this.close();
      }

      // Module click
      if (e.target.closest('.lb-module')) {
        const moduleEl = e.target.closest('.lb-module');
        const moduleId = moduleEl.dataset.moduleId;
        if (moduleId && !moduleEl.classList.contains('is-disabled')) {
          this.selectModule(moduleId);
        }
      }

      // Color swatch click
      if (e.target.closest('.lb-swatch')) {
        const swatch = e.target.closest('.lb-swatch');
        const colorKey = swatch.dataset.colorKey;
        if (colorKey && this.state.selectedModule) {
          // Immediately highlight clicked swatch for visual feedback
          document.querySelectorAll('.lb-swatch').forEach(el => el.classList.remove('is-selected'));
          swatch.classList.add('is-selected');
          this.setModuleColor(this.state.selectedModule, colorKey);
        }
      }

      // Cart: View Customization button
      if (e.target.closest('[data-light-bar-view]')) {
        const btn = e.target.closest('[data-light-bar-view]');
        try {
          const configData = JSON.parse(btn.dataset.config);
          const productTitle = btn.dataset.productTitle;
          const specialInstructions = btn.dataset.specialInstructions || '';
          const lineKey = btn.dataset.lineKey || '';
          this.viewCustomization(configData, productTitle, specialInstructions, lineKey);
        } catch (err) {
          console.error('Failed to parse light bar config:', err);
        }
      }

      // Cart: Edit Attributes button - opens directly to accessories screen
      if (e.target.closest('[data-light-bar-edit]')) {
        const btn = e.target.closest('[data-light-bar-edit]');
        try {
          const configData = JSON.parse(btn.dataset.config);
          const productTitle = btn.dataset.productTitle;
          const variantId = btn.dataset.variantId;
          const productPrice = parseFloat(btn.dataset.productPrice) || 0;
          const lightBarKey = btn.dataset.lightBarKey;
          const productId = btn.dataset.productId || null;

          // Close cart drawer if open
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.close === 'function') {
            cartDrawer.close();
          }

          // Fetch cart to find previously selected accessories/brackets
          if (lightBarKey) {
            this.fetchCartAndPrecheck(lightBarKey).then(() => {
              this.openWithConfig(configData, variantId, productTitle, productPrice, true, lightBarKey, productId);
            });
          } else {
            // No key, just open normally
            this.openWithConfig(configData, variantId, productTitle, productPrice, true, null, productId);
          }
        } catch (err) {
          console.error('Failed to parse light bar config for edit:', err);
        }
      }

      // Step header click (go back to previous step)
      if (e.target.closest('.lb-step__header[data-step]')) {
        const header = e.target.closest('.lb-step__header[data-step]');
        const stepNum = parseInt(header.dataset.step, 10);
        if (stepNum && stepNum !== this.state.currentStep) {
          this.goToStep(stepNum);
        }
      }

      // Accessory click
      if (e.target.closest('.lb-accessory')) {
        const accessory = e.target.closest('.lb-accessory');
        const accessoryId = accessory.dataset.accessoryId;
        if (accessoryId) {
          this.toggleAccessory(accessoryId);
        }
      }

      // Next button
      if (e.target.closest('[data-action="next"]')) {
        this.nextStep();
      }

      // Back button
      if (e.target.closest('[data-action="back"]')) {
        this.prevStep();
      }

      // Add to cart
      if (e.target.closest('[data-action="add-to-cart"]')) {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart();
      }

      // Print
      if (e.target.closest('[data-action="print"]')) {
        this.print();
      }

      // Quantity buttons
      if (e.target.closest('[data-action="qty-minus"]')) {
        this.updateQuantity(-1);
      }
      if (e.target.closest('[data-action="qty-plus"]')) {
        this.updateQuantity(1);
      }

      // Price info tooltip - mobile tap toggle
      const priceInfo = e.target.closest('.lb-builder__price-info');
      if (priceInfo) {
        priceInfo.classList.toggle('is-active');
      } else {
        // Close tooltip when tapping elsewhere
        document.querySelectorAll('.lb-builder__price-info.is-active').forEach(el => {
          el.classList.remove('is-active');
        });
      }
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.close();
      }
    });

    // Special instructions input
    document.addEventListener('input', (e) => {
      if (e.target.matches('.lb-review__instructions-input')) {
        this.state.specialInstructions = e.target.value;
      }
    });
  }

  // ========================================
  // Public API
  // ========================================

  open(productId, variantId, productTitle, basePrice, configKey) {
    if (!this.configs) {
      console.error('Configs not loaded');
      return;
    }

    const config = this.configs.products[configKey];
    if (!config) {
      console.error('Config not found:', configKey);
      return;
    }

    // Debug: Log which model and layout is being used
    console.log('[LightBarBuilder] Opening:', {
      configKey,
      layout: config.layout,
      title: config.title,
      steps: config.steps?.length
    });

    // Reset state
    this.state = {
      ...this.state,
      isOpen: true,
      productId,
      variantId,
      productTitle,
      basePrice,
      configKey,
      config,
      currentStep: 1,
      currentScreen: 'configure',
      selectedModule: null,
      moduleColors: {},
      accessories: [],
      brackets: [],
      quantity: 1,
      specialInstructions: '',
      editingLightBarKey: null // Not editing from cart
    };

    // Render
    this.render();

    // Auto-select first module in step 1
    this.autoSelectNextModule();

    // Show builder
    document.body.classList.add('overflow-hidden');
    this.elements.builder?.classList.add('is-open');
  }

  close() {
    this.state.isOpen = false;

    // Tear down any off-PDP staged Rebuy widgets and restore the page's product context.
    this.teardownRebuyStage();

    // Hide the builder
    const builder = this.elements.builder || document.querySelector('.lb-builder');
    if (builder) {
      builder.classList.remove('is-open');
    }

    // Reset body styles completely
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }

  // Open builder with existing configuration (for Edit Attributes from cart)
  // skipToAccessories: if true, opens directly to accessories screen
  // editingLightBarKey: if set, we're editing an existing cart item (will remove old items before adding)
  openWithConfig(configData, variantId, productTitle, basePrice, skipToAccessories = false, editingLightBarKey = null, productId = null) {
    if (!this.configs) {
      console.error('Configs not loaded');
      return;
    }

    const config = this.configs.products[configData.configKey];
    if (!config) {
      console.error('Config not found:', configData.configKey);
      return;
    }

    // Set state with saved configuration.
    // productId (from the cart line / PDP) lets us stage the Rebuy accessory/bracket widgets
    // when the shopper is NOT on this product's PDP (e.g. editing from the cart page or a
    // drawer opened elsewhere) — see ensureRebuyStaged().
    this.state = {
      ...this.state,
      isOpen: true,
      productId: productId || null,
      variantId,
      productTitle,
      basePrice,
      configKey: configData.configKey,
      config,
      currentStep: config.steps.length, // Set to last step when skipping to accessories
      currentScreen: skipToAccessories ? 'accessories' : 'configure',
      selectedModule: null,
      moduleColors: configData.moduleColors || {},
      accessories: [],
      brackets: [],
      quantity: 1,
      specialInstructions: '',
      editingLightBarKey: editingLightBarKey // Track if we're editing from cart
    };

    // Render
    this.render();

    // Only auto-select module if on configure screen
    if (!skipToAccessories) {
      this.state.currentStep = 1;
      this.autoSelectNextModule();
    }

    // Show builder
    document.body.classList.add('overflow-hidden');
    this.elements.builder?.classList.add('is-open');
  }

  // ========================================
  // Module Selection
  // ========================================

  selectModule(moduleId) {
    const { config, currentStep } = this.state;

    // Find which step this module belongs to
    let moduleStep = null;
    for (let i = 0; i < config.steps.length; i++) {
      if (config.steps[i].modules.includes(moduleId)) {
        moduleStep = i + 1;
        break;
      }
    }

    if (!moduleStep) return;

    // If module is from a different step, switch to that step
    if (moduleStep !== currentStep) {
      this.goToStep(moduleStep);
    }

    this.state.selectedModule = moduleId;
    this.renderModules();
    this.renderSidebar();
    this.highlightSelectedSwatch();
  }

  goToStep(stepNumber) {
    const { config } = this.state;
    if (stepNumber < 1 || stepNumber > config.steps.length) return;

    this.state.currentStep = stepNumber;
    this.state.selectedModule = null;
    this.renderModules();
    this.renderSidebar();
    this.updateNextButton();
    this.autoSelectNextModule();
  }

  setModuleColor(moduleId, colorKey) {
    this.state.moduleColors[moduleId] = colorKey;
    this.state.selectedModule = null;

    this.renderModules();
    this.updateStepProgress();
    this.updateNextButton();

    // Auto-select next unconfigured module in current step
    this.autoSelectNextModule();
  }

  autoSelectNextModule() {
    const { config, currentStep, moduleColors } = this.state;
    const step = config.steps[currentStep - 1];

    const nextModule = step.modules.find(m => !moduleColors[m]);
    if (nextModule) {
      this.state.selectedModule = nextModule;
      this.renderModules();
    }
  }

  // ========================================
  // Step Navigation
  // ========================================

  nextStep() {
    const { config, currentStep, currentScreen } = this.state;

    if (currentScreen === 'configure') {
      if (currentStep < config.steps.length) {
        // Go to next step
        this.state.currentStep = currentStep + 1;
        this.state.selectedModule = null;
        this.render();
        this.autoSelectNextModule();
      } else {
        // Go to accessories screen (includes brackets)
        this.state.currentScreen = 'accessories';
        this.render();
      }
    } else if (currentScreen === 'accessories') {
      // Go directly to review screen (brackets are now part of accessories)
      this.state.currentScreen = 'review';
      this.render();
    }
  }

  prevStep() {
    const { currentStep, currentScreen } = this.state;

    if (currentScreen === 'review') {
      // "Back to Edit" from review goes directly to Step 1 (per QA)
      this.state.currentScreen = 'configure';
      this.state.currentStep = 1;
      this.state.selectedModule = null;
      this.render();
      this.autoSelectNextModule();
    } else if (currentScreen === 'accessories') {
      this.state.currentScreen = 'configure';
      this.render();
    } else if (currentStep > 1) {
      this.state.currentStep = currentStep - 1;
      this.state.selectedModule = null;
      this.render();
    }
  }

  isStepComplete(stepIndex) {
    const { config, moduleColors } = this.state;
    const step = config.steps[stepIndex];
    // Defensive check for invalid step index
    if (!step || !step.modules || step.modules.length === 0) return false;
    return step.modules.every(m => !!moduleColors[m]);
  }

  // ========================================
  // Accessories
  // ========================================

  toggleAccessory(accessoryId) {
    const index = this.state.accessories.findIndex(a => a.id === accessoryId);
    if (index > -1) {
      this.state.accessories.splice(index, 1);
    } else {
      // Get accessory data from DOM
      const el = document.querySelector(`[data-accessory-id="${accessoryId}"]`);
      if (el) {
        this.state.accessories.push({
          id: accessoryId,
          variantId: el.dataset.variantId,
          title: el.dataset.title,
          price: parseFloat(el.dataset.price) || 0
        });
      }
    }
    this.renderAccessories();
    this.updateTotalPrice();
  }

  // ========================================
  // Quantity
  // ========================================

  updateQuantity(delta) {
    const newQty = Math.max(1, this.state.quantity + delta);
    this.state.quantity = newQty;
    this.renderReview();
  }

  // ========================================
  // Cart
  // ========================================

  /**
   * Fetch cart and build precheck list from previously selected accessories/brackets
   * Called when editing a light bar from cart
   */
  /**
   * Remove old light bar items from cart when editing
   * Removes the parent item and all linked accessories/brackets
   */
  async removeOldLightBarItems(lightBarKey) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Find all items to remove (parent + accessories with this key)
      const updates = {};
      cart.items.forEach(item => {
        const itemKey = item.properties?.['_light_bar_key'];
        const parentKey = item.properties?.['_parent_light_bar_key'];

        if (itemKey === lightBarKey || parentKey === lightBarKey) {
          // Set quantity to 0 to remove
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        console.log('No items to remove for key:', lightBarKey);
        return;
      }

      console.log('Removing old items:', updates);

      // Use cart update API to remove items
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      console.log('Old items removed successfully');
    } catch (err) {
      console.error('Failed to remove old light bar items:', err);
    }
  }

  async fetchCartAndPrecheck(lightBarKey) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Find items linked to this light bar
      const precheckIds = [];
      cart.items.forEach(item => {
        const parentKey = item.properties?.['_parent_light_bar_key'];
        if (parentKey === lightBarKey) {
          // This is an accessory/bracket for our light bar
          precheckIds.push('v' + item.variant_id);
        }
      });

      // Store for use by applyRebuyPrecheck
      // Merge with existing STLAddonPrecheck (included items from metafield)
      const existingPrecheck = window.STLAddonPrecheck || [];
      window.STLCartPrecheck = precheckIds;
      window.STLAddonPrecheck = [...new Set([...existingPrecheck, ...precheckIds])];

      console.log('Cart precheck IDs:', precheckIds);
      console.log('Combined precheck IDs:', window.STLAddonPrecheck);
    } catch (err) {
      console.error('Failed to fetch cart for precheck:', err);
    }
  }

  async addToCart() {
    const { variantId, quantity, moduleColors, accessories, brackets, specialInstructions, config, editingLightBarKey } = this.state;

    console.log('addToCart called:', { variantId, quantity, accessories, editingLightBarKey });

    if (!variantId) {
      console.error('No variant ID set');
      return;
    }

    // If editing an existing cart item, remove old items first
    if (editingLightBarKey) {
      await this.removeOldLightBarItems(editingLightBarKey);
    }

    // Generate a unique key to link parent item with its accessories
    // Use the same key if editing to maintain consistency, or generate new one
    const lightBarKey = editingLightBarKey || `lb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Build line item properties
    const properties = {};

    // Store configuration data (needed for View Customization link to work)
    const configData = {
      configKey: this.state.configKey,
      layout: config.layout,
      moduleColors: moduleColors,
      productTitle: this.state.productTitle
    };

    // Generate viewer URL (hidden with _ prefix, appears first in order admin)
    const viewerConfig = btoa(JSON.stringify(configData));
    const viewerUrl = `${window.location.origin}/pages/light-bar-viewer?config=${viewerConfig}`;
    properties['_View Customization'] = viewerUrl;

    // Add special instructions if any (visible to customer)
    if (specialInstructions.trim()) {
      properties['Special Instructions'] = specialInstructions.trim();
    }

    // Hidden config data (required for viewer to decode the configuration - must keep)
    properties['_light_bar_config'] = JSON.stringify(configData);

    // Add unique key to link with accessories (hidden property)
    properties['_light_bar_key'] = lightBarKey;

    // Build cart items - ensure IDs are numbers
    const items = [
      {
        id: parseInt(variantId, 10),
        quantity: quantity,
        properties: properties
      }
    ];

    // Add accessories with reference to parent light bar
    // Use per-accessory qty from STLAddonQty (e.g., 3 brackets per light bar) * main qty
    // Resolution: variant key first, then product key fallback, then 1
    const addonQtyMap = window.STLAddonQty || {};
    accessories.forEach(acc => {
      const variantKey = 'v' + acc.variantId;
      const productKey = acc.productId ? 'p' + acc.productId : null;
      const perUnitQty = addonQtyMap[variantKey] || (productKey && addonQtyMap[productKey]) || 1;
      items.push({
        id: parseInt(acc.variantId, 10),
        quantity: perUnitQty * quantity,
        // Native nested cart line: child references the parent light bar by its variant id in this
        // same /cart/add.js request. Kept alongside _parent_light_bar_key (which still drives the
        // cart grouping/edit/count) so the ORDER carries a real parent/child LineItemGroup for ERP.
        parent_id: parseInt(variantId, 10),
        properties: {
          '_parent_light_bar_key': lightBarKey,
          '_accessory_title': acc.title
        }
      });
    });

    // Add brackets with reference to parent light bar
    // Same quantity logic as accessories
    brackets.forEach(bracket => {
      const variantKey = 'v' + bracket.variantId;
      const productKey = bracket.productId ? 'p' + bracket.productId : null;
      const perUnitQty = addonQtyMap[variantKey] || (productKey && addonQtyMap[productKey]) || 1;
      items.push({
        id: parseInt(bracket.variantId, 10),
        quantity: perUnitQty * quantity,
        // Native nested cart line (same as accessories above): nest the bracket under the parent.
        parent_id: parseInt(variantId, 10),
        properties: {
          '_parent_light_bar_key': lightBarKey,
          '_accessory_title': bracket.title
        }
      });
    });

    // Get the cart element (cart-drawer or cart-notification)
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Build sections to request (same way theme does it)
    const sectionsToRender = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender().map(section => section.id)
      : ['cart-drawer', 'cart-icon-bubble'];

    try {
      // Build JSON payload for cart add
      const cartUrl = window.routes?.cart_add_url || '/cart/add.js';

      console.log('Adding to cart:', { variantId, quantity, items, cartUrl });

      const payload = {
        items: items,
        sections: sectionsToRender,
        sections_url: window.location.pathname
      };

      const response = await fetch(cartUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Check if response is ok
      if (!response.ok) {
        const text = await response.text();
        console.error('Cart response error:', response.status, text.substring(0, 200));
        return;
      }

      const responseData = await response.json();

      if (responseData.status) {
        // Error occurred
        console.error('Cart error:', responseData.description || responseData.message);
        return;
      }

      // Close the builder first
      this.close();

      // Wait for builder to fully close and body styles to reset
      await new Promise(resolve => setTimeout(resolve, 300));

      // Re-query cart drawer after delay
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Remove is-empty class if cart was empty (same as product-form.js)
        if (cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }

        // Reset drawer state - remove any existing active/animate classes
        // so renderContents can properly trigger the open animation
        cartDrawer.classList.remove('active', 'animate');

        // Small delay to ensure classes are cleared before rendering
        await new Promise(resolve => setTimeout(resolve, 50));

        // renderContents updates the drawer HTML and calls open() internally
        cartDrawer.renderContents(responseData);
      } else {
        // Fallback: go to cart page
        window.location = window.routes?.cart_url || '/cart';
      }

    } catch (error) {
      console.error('Cart error:', error);
    }
  }

  // ========================================
  // Print
  // ========================================

  print() {
    // Open print in new tab with diagram
    this.openPrintPage();
  }

  openPrintPage() {
    const { productTitle, config, moduleColors } = this.state;

    // Get the current SVG
    const svgElement = this.elements.diagram?.querySelector('svg');
    if (!svgElement) return;

    // Clone and serialize the SVG
    const svgClone = svgElement.cloneNode(true);
    const svgString = new XMLSerializer().serializeToString(svgClone);

    // Create print page HTML with exact same styles as picker
    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${productTitle || 'Light Bar Configuration'}</title>
        <style>
          :root {
            --lb-green: #2E7D32;
            --lb-dark: #011028;
            --lb-gray-bg: #F2F2F2;
            --lb-white: #FFFFFF;
          }
          body {
            font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
            padding: 40px;
            max-width: 1000px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 30px;
            color: #011028;
          }
          .diagram {
            display: flex;
            justify-content: center;
            margin-bottom: 30px;
          }
          .diagram svg {
            max-width: 100%;
            height: auto;
          }
          /* Module styles - exact replica of picker */
          .lb-module__shape {
            fill: var(--lb-gray-bg);
            stroke: var(--lb-dark);
            stroke-width: 1;
            rx: 2;
            ry: 2;
          }
          .lb-module.is-configured .lb-module__shape {
            stroke: var(--lb-dark);
            stroke-width: 1;
          }
          .lb-module__label {
            font-size: 14px;
            font-weight: 600;
            fill: var(--lb-dark);
            text-anchor: middle;
            dominant-baseline: middle;
          }
          .lb-module__function-label {
            font-size: 14px;
            font-weight: 700;
            fill: var(--lb-dark);
            text-anchor: middle;
            dominant-baseline: middle;
          }
          .note {
            font-size: 13px;
            color: #011028;
            margin-bottom: 20px;
          }
          .legend {
            border-top: 1px solid #e5e5e5;
            padding-top: 20px;
          }
          .legend h3 {
            font-size: 16px;
            margin-bottom: 10px;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <h1>${productTitle || 'Custom Light Bar Configuration'}</h1>
        <div class="diagram">${svgString}</div>
        ${config?.note ? `<p class="note"><strong>Note:</strong> ${config.note}</p>` : ''}
        <div class="legend">
          <h3>Legend</h3>
          ${config?.legend?.lightBar ? `<p><strong>Light Bar:</strong> ${config.legend.lightBar}</p>` : ''}
          ${config?.legend?.modules ? `<p><strong>Modules:</strong> ${config.legend.modules}</p>` : ''}
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    // Open in new tab
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printHtml);
      printWindow.document.close();
    }
  }

  // ========================================
  // Cart Customization View
  // ========================================

  async viewCustomization(configData, productTitle, specialInstructions = '', lineKey = '') {
    const modal = document.getElementById('light-bar-customization-modal');
    if (!modal) {
      console.error('Modal not found');
      return;
    }

    const titleEl = modal.querySelector('.lb-customization-modal__title');
    const diagramEl = modal.querySelector('.lb-customization-modal__diagram');
    const printBtn = modal.querySelector('.lb-customization-modal__print');
    const saveBtn = modal.querySelector('.lb-customization-modal__save');
    const instructionsInput = modal.querySelector('.lb-customization-modal__instructions-input');

    if (titleEl) titleEl.textContent = productTitle;

    // Pre-populate special instructions textarea
    if (instructionsInput) {
      instructionsInput.value = specialInstructions || '';
    }

    // Store line key for save functionality
    modal.dataset.lineKey = lineKey;

    // Show loading state
    if (diagramEl) {
      diagramEl.innerHTML = '<p>Loading...</p>';
    }

    // Ensure configs are loaded
    if (!this.configs) {
      console.log('Loading configs...');
      await this.loadConfigs();
    }

    console.log('Config data received:', configData);
    console.log('Configs available:', this.configs ? 'Yes' : 'No');

    // Render the diagram based on config data
    if (diagramEl && this.configs) {
      const layout = this.configs.layouts[configData.layout];
      console.log('Layout found:', layout ? 'Yes' : 'No', 'for key:', configData.layout);
      console.log('Module colors:', configData.moduleColors);

      if (layout) {
        const svg = this.createStaticDiagramSVG(layout, configData.moduleColors);
        console.log('SVG created:', svg);
        console.log('SVG innerHTML:', svg.innerHTML.substring(0, 500));
        diagramEl.innerHTML = '';
        diagramEl.appendChild(svg);
        console.log('SVG appended to diagram');
      } else {
        console.error('Layout not found:', configData.layout);
        diagramEl.innerHTML = '<p>Unable to load configuration - layout not found</p>';
      }
    } else {
      console.error('Missing diagramEl or configs');
      if (diagramEl) {
        diagramEl.innerHTML = '<p>Unable to load configuration</p>';
      }
    }

    // Setup print button - uses current textarea value
    if (printBtn) {
      printBtn.onclick = () => {
        const currentInstructions = instructionsInput ? instructionsInput.value : '';
        this.printCustomization(productTitle, diagramEl, currentInstructions);
      };
    }

    // Setup save button
    if (saveBtn) {
      saveBtn.onclick = async () => {
        const currentLineKey = modal.dataset.lineKey;
        const newInstructions = instructionsInput ? instructionsInput.value.trim() : '';

        if (!currentLineKey) {
          console.error('No line key found for saving');
          return;
        }

        await this.saveSpecialInstructions(currentLineKey, newInstructions, saveBtn);
      };
    }

    // Show modal
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Setup close handlers
    const closeBtn = modal.querySelector('.lb-customization-modal__close');
    const overlay = modal.querySelector('.lb-customization-modal__overlay');

    const closeModal = () => {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    closeBtn?.addEventListener('click', closeModal, { once: true });
    overlay?.addEventListener('click', closeModal, { once: true });
  }

  async saveSpecialInstructions(lineKey, instructions, saveBtn) {
    // Show loading state
    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Saving...';
    saveBtn.disabled = true;

    try {
      // Get current cart to find the item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find the item by key
      const item = cart.items.find(i => i.key === lineKey);
      if (!item) {
        console.error('Cart item not found:', lineKey);
        saveBtn.textContent = 'Error';
        setTimeout(() => {
          saveBtn.textContent = originalText;
          saveBtn.disabled = false;
        }, 2000);
        return;
      }

      // Build updated properties - keep existing ones, update Special Instructions
      const updatedProperties = { ...item.properties };
      if (instructions) {
        updatedProperties['Special Instructions'] = instructions;
      } else {
        // Remove if empty
        delete updatedProperties['Special Instructions'];
      }

      // Update the cart item
      const updateResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          properties: updatedProperties
        })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update cart');
      }

      // Show success
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 2000);

      // Update the data attribute on the View button for future opens
      const viewBtn = document.querySelector(`[data-light-bar-view][data-line-key="${lineKey}"]`);
      if (viewBtn) {
        viewBtn.dataset.specialInstructions = instructions;
      }

    } catch (error) {
      console.error('Error saving special instructions:', error);
      saveBtn.textContent = 'Error';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 2000);
    }
  }

  printCustomization(productTitle, diagramEl, specialInstructions = '') {
    const svgElement = diagramEl?.querySelector('svg');
    if (!svgElement) return;

    const svgClone = svgElement.cloneNode(true);
    const svgString = new XMLSerializer().serializeToString(svgClone);

    // Build special instructions HTML if present
    const instructionsHtml = specialInstructions && specialInstructions.trim()
      ? `<div class="instructions">
          <h3>Special Instructions</h3>
          <p>${specialInstructions.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>
        </div>`
      : '';

    // Print with exact same styles as picker
    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${productTitle || 'Light Bar Configuration'}</title>
        <style>
          :root {
            --lb-green: #2E7D32;
            --lb-dark: #011028;
            --lb-gray-bg: #F2F2F2;
            --lb-white: #FFFFFF;
          }
          body {
            font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
            padding: 40px;
            max-width: 1000px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 30px;
            color: #011028;
          }
          .diagram {
            display: flex;
            justify-content: center;
          }
          .diagram svg {
            max-width: 100%;
            height: auto;
          }
          /* Module styles - exact replica of picker */
          .lb-module__shape {
            fill: var(--lb-gray-bg);
            stroke: var(--lb-dark);
            stroke-width: 1;
            rx: 2;
            ry: 2;
          }
          .lb-module.is-configured .lb-module__shape {
            stroke: var(--lb-dark);
            stroke-width: 1;
          }
          .lb-module__label {
            font-size: 14px;
            font-weight: 600;
            fill: var(--lb-dark);
            text-anchor: middle;
            dominant-baseline: middle;
          }
          .lb-module__function-label {
            font-size: 14px;
            font-weight: 700;
            fill: var(--lb-dark);
            text-anchor: middle;
            dominant-baseline: middle;
          }
          .instructions {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e5e5;
          }
          .instructions h3 {
            font-size: 16px;
            font-weight: 600;
            color: #011028;
            margin: 0 0 10px 0;
          }
          .instructions p {
            font-size: 14px;
            color: #333;
            line-height: 1.5;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <h1>${productTitle || 'Custom Light Bar Configuration'}</h1>
        <div class="diagram">${svgString}</div>
        ${instructionsHtml}
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printHtml);
      printWindow.document.close();
    }
  }

  createStaticDiagramSVG(layout, moduleColors) {
    // Create SVG with the saved colors (for View Customization)
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', layout.viewBox);
    svg.setAttribute('class', 'lb-diagram__svg');
    svg.style.maxWidth = '100%';
    svg.style.height = 'auto';

    // Create defs for gradients
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.appendChild(defs);

    // Create modules with colors pre-applied
    layout.modules.forEach(module => {
      const group = this.createStaticModuleElement(module, moduleColors[module.id], defs);
      svg.appendChild(group);
    });

    // Add center dash for split-visor layouts
    if (layout.type === 'split-visor') {
      const viewBoxParts = layout.viewBox.split(' ').map(Number);
      const width = viewBoxParts[2];
      const height = viewBoxParts[3];
      const centerX = width / 2;
      const centerY = height / 2;

      const dash = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      dash.setAttribute('x1', centerX - 30);
      dash.setAttribute('y1', centerY);
      dash.setAttribute('x2', centerX + 30);
      dash.setAttribute('y2', centerY);
      dash.setAttribute('stroke', '#011028');
      dash.setAttribute('stroke-width', '3');
      dash.setAttribute('stroke-linecap', 'round');
      svg.appendChild(dash);
    }

    return svg;
  }

  createStaticModuleElement(module, colorKey, defs) {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    const cx = module.x + module.width / 2;
    const cy = module.y + module.height / 2;
    const rotation = module.rotation || 0;

    // Get color data
    let topColor = '#F2F2F2';
    let bottomColor = '#F2F2F2';

    // Fixed colors for STT (solid red) and RWL (white)
    if (module.type === 'stt') {
      topColor = '#DB3735'; // Red
      bottomColor = '#DB3735'; // Red (solid)
    } else if (module.type === 'rwl') {
      topColor = '#F2F2F2'; // Off-white for visibility
      bottomColor = '#F2F2F2'; // Off-white for visibility
    } else if (colorKey) {
      const colorData = this.getColorData(colorKey);
      console.log(`Module ${module.id}: colorKey=${colorKey}, colorData=`, colorData);
      if (colorData) {
        // Dual colors use primary/secondary, single colors use hex
        // Use getModuleFillColor to convert white to off-white for better visibility
        topColor = this.getModuleFillColor(colorData.primary || colorData.hex) || '#F2F2F2';
        bottomColor = this.getModuleFillColor(colorData.secondary || colorData.hex) || topColor;

        // Swap colors for modules rotated more than 90° (bottom elements)
        // so primary color appears at visual "top" of the rotated module
        if (Math.abs(rotation) > 90 && colorData.primary && colorData.secondary) {
          [topColor, bottomColor] = [bottomColor, topColor];
        }

        console.log(`  -> topColor=${topColor}, bottomColor=${bottomColor}`);
      } else {
        console.warn(`  -> No color data found for key: ${colorKey}`);
      }
    }

    // Create gradient
    const gradientId = `static-gradient-${module.id}`;
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', gradientId);
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse');
    gradient.setAttribute('x1', cx);
    gradient.setAttribute('y1', module.y);
    gradient.setAttribute('x2', cx);
    gradient.setAttribute('y2', module.y + module.height);

    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('stop-color', topColor);

    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '50%');
    stop2.setAttribute('stop-color', topColor);

    const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop3.setAttribute('offset', '50%');
    stop3.setAttribute('stop-color', bottomColor);

    const stop4 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop4.setAttribute('offset', '100%');
    stop4.setAttribute('stop-color', bottomColor);

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    gradient.appendChild(stop3);
    gradient.appendChild(stop4);
    defs.appendChild(gradient);

    // Create fill rect
    const fillRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    fillRect.setAttribute('x', module.x);
    fillRect.setAttribute('y', module.y);
    fillRect.setAttribute('width', module.width);
    fillRect.setAttribute('height', module.height);
    fillRect.setAttribute('rx', '2');
    fillRect.setAttribute('fill', `url(#${gradientId})`);

    if (rotation) {
      fillRect.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
    }
    group.appendChild(fillRect);

    // Create border
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', module.x);
    rect.setAttribute('y', module.y);
    rect.setAttribute('width', module.width);
    rect.setAttribute('height', module.height);
    rect.setAttribute('rx', '2');
    rect.setAttribute('fill', 'none');
    rect.setAttribute('stroke', '#011028');
    rect.setAttribute('stroke-width', '1');

    if (rotation) {
      rect.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
    }
    group.appendChild(rect);

    // Add label
    if (module.label) {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', cx);
      text.setAttribute('y', cy);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('font-size', '14');
      text.setAttribute('font-weight', '600');
      text.setAttribute('fill', '#011028');

      if (rotation) {
        text.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
      }

      text.textContent = module.label;
      group.appendChild(text);
    }

    // Add function label (TD, A) if applicable
    if (colorKey) {
      const colorData = this.getColorData(colorKey);
      if (colorData && colorData.description) {
        const funcText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        funcText.setAttribute('x', cx);
        funcText.setAttribute('y', cy);
        funcText.setAttribute('text-anchor', 'middle');
        funcText.setAttribute('dominant-baseline', 'middle');
        funcText.setAttribute('font-size', '14');
        funcText.setAttribute('font-weight', '700');
        funcText.setAttribute('fill', '#011028');

        if (rotation) {
          funcText.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
        }

        funcText.textContent = colorData.label;
        group.appendChild(funcText);
      }
    }

    // Traffic advisor arrows - only show if NOT a function color (TD, A)
    if (module.type === 'traffic-advisor') {
      const colorData = colorKey ? this.getColorData(colorKey) : null;
      const isFunctionColor = colorData && colorData.description;

      // Only show arrows if not TD or A
      if (!isFunctionColor) {
        const arrowScale = Math.min(module.width / 80, module.height / 25) * 0.6;
        const arrowX = module.x + (module.width - 74 * arrowScale) / 2;
        const arrowY = module.y + (module.height - 21 * arrowScale) / 2;

        const arrowGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        arrowGroup.setAttribute('transform', `translate(${arrowX}, ${arrowY}) scale(${arrowScale})`);

        // Simplified arrow paths
        const leftArrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        leftArrow.setAttribute('d', 'M0 10.5L12 0v7h15v7H12v7L0 10.5z');
        leftArrow.setAttribute('fill', '#011028');
        arrowGroup.appendChild(leftArrow);

        const rightArrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        rightArrow.setAttribute('d', 'M74 10.5L62 0v7H47v7h15v7l12-10.5z');
        rightArrow.setAttribute('fill', '#011028');
        arrowGroup.appendChild(rightArrow);

        group.appendChild(arrowGroup);
      }
    }

    return group;
  }

  // ========================================
  // Rendering
  // ========================================

  render() {
    this.renderHeader();
    this.renderDiagram();
    this.renderNote();
    this.renderLegend();
    this.renderSidebar();
  }

  renderHeader() {
    const { productTitle, basePrice, configKey, config } = this.state;

    if (this.elements.title) {
      this.elements.title.textContent = productTitle;
    }
    if (this.elements.price) {
      this.elements.price.textContent = this.formatPrice(basePrice);
    }

    // Debug: Show model/layout info in HTML
    let debugEl = document.querySelector('.lb-builder__debug');
    if (!debugEl) {
      debugEl = document.createElement('div');
      debugEl.className = 'lb-builder__debug';
      debugEl.style.cssText = 'font-size: 11px; color: #888; font-family: monospace; margin-top: 4px;';
      this.elements.title?.parentNode?.appendChild(debugEl);
    }
    debugEl.textContent = `Config: ${configKey} | Layout: ${config?.layout}`;
  }

  renderDiagram() {
    const { config } = this.state;
    const layout = this.configs.layouts[config.layout];

    if (!this.elements.diagram) return;

    const svg = this.createDiagramSVG(layout);
    this.elements.diagram.innerHTML = '';
    this.elements.diagram.appendChild(svg);

    this.renderModules();
  }

  createDiagramSVG(layout) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', layout.viewBox);
    svg.setAttribute('class', 'lb-diagram__svg');
    svg.setAttribute('data-layout-type', layout.type);

    // Add defs for gradients (used for dual colors)
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.appendChild(defs);

    layout.modules.forEach(module => {
      const group = this.createModuleElement(module, svg);
      svg.appendChild(group);
    });

    // Add center dash for split-visor layouts
    if (layout.type === 'split-visor') {
      const viewBoxParts = layout.viewBox.split(' ').map(Number);
      const width = viewBoxParts[2];
      const height = viewBoxParts[3];
      const centerX = width / 2;
      const centerY = height / 2;

      const dash = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      dash.setAttribute('x1', centerX - 30);
      dash.setAttribute('y1', centerY);
      dash.setAttribute('x2', centerX + 30);
      dash.setAttribute('y2', centerY);
      dash.setAttribute('stroke', '#011028');
      dash.setAttribute('stroke-width', '3');
      dash.setAttribute('stroke-linecap', 'round');
      svg.appendChild(dash);
    }

    return svg;
  }

  createModuleElement(module, svg) {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('class', 'lb-module');
    group.setAttribute('data-module-id', module.id);
    group.setAttribute('data-module-type', module.type);

    // Mark fixed modules (STT, RWL) as non-configurable
    const isFixedModule = module.type === 'stt' || module.type === 'rwl';
    if (isFixedModule) {
      group.classList.add('is-fixed');
    }

    const cx = module.x + module.width / 2;
    const cy = module.y + module.height / 2;
    const rotation = module.rotation || 0;

    // Create a unique gradient for this module
    // Use userSpaceOnUse so we can properly position and rotate the gradient
    const gradientId = `gradient-${module.id}`;
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', gradientId);
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse');

    // Gradient goes from top to bottom of the module
    gradient.setAttribute('x1', cx);
    gradient.setAttribute('y1', module.y);
    gradient.setAttribute('x2', cx);
    gradient.setAttribute('y2', module.y + module.height);

    // Fixed colors for STT (red/amber) and RWL (white)
    let topColor = 'white';
    let bottomColor = 'white';
    let showFill = false;

    if (module.type === 'stt') {
      topColor = '#DB3735'; // Red
      bottomColor = '#DB3735'; // Red (solid)
      showFill = true;
    } else if (module.type === 'rwl') {
      topColor = '#F2F2F2'; // Off-white for visibility
      bottomColor = '#F2F2F2'; // Off-white for visibility
      showFill = true;
    }

    // Color stops - hard stop at 50% for clean split
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('stop-color', topColor);
    stop1.setAttribute('class', 'lb-gradient-stop-top');

    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '50%');
    stop2.setAttribute('stop-color', topColor);
    stop2.setAttribute('class', 'lb-gradient-stop-top-end');

    const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop3.setAttribute('offset', '50%');
    stop3.setAttribute('stop-color', bottomColor);
    stop3.setAttribute('class', 'lb-gradient-stop-bottom-start');

    const stop4 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop4.setAttribute('offset', '100%');
    stop4.setAttribute('stop-color', bottomColor);
    stop4.setAttribute('class', 'lb-gradient-stop-bottom');

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    gradient.appendChild(stop3);
    gradient.appendChild(stop4);
    svg.querySelector('defs').appendChild(gradient);

    // Create fill rect (uses gradient)
    const fillRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    fillRect.setAttribute('class', 'lb-module__fill');
    fillRect.setAttribute('x', module.x);
    fillRect.setAttribute('y', module.y);
    fillRect.setAttribute('width', module.width);
    fillRect.setAttribute('height', module.height);
    fillRect.setAttribute('rx', '4');
    fillRect.setAttribute('fill', `url(#${gradientId})`);
    fillRect.style.opacity = showFill ? '1' : '0';

    if (rotation) {
      fillRect.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
    }

    group.appendChild(fillRect);

    // Create main shape (border)
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('class', 'lb-module__shape');
    rect.setAttribute('x', module.x);
    rect.setAttribute('y', module.y);
    rect.setAttribute('width', module.width);
    rect.setAttribute('height', module.height);
    rect.setAttribute('rx', '4');

    if (rotation) {
      rect.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
    }

    group.appendChild(rect);

    // Add label or traffic advisor arrows
    if (module.type === 'traffic-advisor') {
      // Traffic advisor uses arrow icon
      const arrowGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      arrowGroup.setAttribute('class', 'lb-module__arrows');

      // Scale and position the arrows inside the module
      const arrowScale = Math.min(module.width / 80, module.height / 25) * 0.6;
      const arrowX = module.x + (module.width - 74 * arrowScale) / 2;
      const arrowY = module.y + (module.height - 21 * arrowScale) / 2;

      arrowGroup.setAttribute('transform', `translate(${arrowX}, ${arrowY}) scale(${arrowScale})`);

      // Left arrow
      const leftArrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      leftArrow.setAttribute('d', 'M13.5 6.39258H31.9766V14.3926H13.5V20.7852L0 10.3926L13.5 0V6.39258Z');
      leftArrow.setAttribute('fill', '#011028');
      leftArrow.setAttribute('class', 'lb-module__arrow');

      // Right arrow
      const rightArrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      rightArrow.setAttribute('d', 'M60.4766 14.3926L42 14.3926L42 6.39258L60.4766 6.39258L60.4766 -1.18021e-06L73.9766 10.3926L60.4766 20.7852L60.4766 14.3926Z');
      rightArrow.setAttribute('fill', '#011028');
      rightArrow.setAttribute('class', 'lb-module__arrow');

      arrowGroup.appendChild(leftArrow);
      arrowGroup.appendChild(rightArrow);
      group.appendChild(arrowGroup);
    } else if (module.label) {
      // Regular label
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('class', 'lb-module__label');
      text.setAttribute('x', cx);
      text.setAttribute('y', cy);
      text.textContent = module.label;
      group.appendChild(text);
    }

    return group;
  }

  renderModules() {
    const { config, currentStep, selectedModule, moduleColors } = this.state;
    const step = config.steps[currentStep - 1];

    document.querySelectorAll('.lb-module').forEach(el => {
      const moduleId = el.dataset.moduleId;
      const moduleType = el.dataset.moduleType;
      const isFixedModule = moduleType === 'stt' || moduleType === 'rwl';
      const isInCurrentStep = step.modules.includes(moduleId);
      const isSelected = moduleId === selectedModule;
      const isConfigured = !!moduleColors[moduleId];

      // Fixed modules (STT, RWL) are always disabled and shown as configured
      if (isFixedModule) {
        el.classList.remove('is-selectable', 'is-selected');
        el.classList.add('is-disabled', 'is-configured', 'is-fixed');
        return;
      }

      // Modules are selectable if in current step and not yet configured
      el.classList.toggle('is-selectable', isInCurrentStep && !isConfigured);
      el.classList.toggle('is-selected', isSelected);
      el.classList.toggle('is-configured', isConfigured);
      // Only disable modules that are NOT configured AND NOT in current step
      // Configured modules should always be clickable (to allow editing previous steps)
      el.classList.toggle('is-disabled', !isInCurrentStep && !isConfigured);

      // Update fill color
      if (isConfigured) {
        const colorKey = moduleColors[moduleId];
        this.applyModuleColor(el, colorKey);
      }
    });
  }

  applyModuleColor(moduleEl, colorKey) {
    const colorData = this.getColorData(colorKey);
    const moduleId = moduleEl.dataset.moduleId;
    const fillRect = moduleEl.querySelector('.lb-module__fill');
    const shape = moduleEl.querySelector('.lb-module__shape');

    if (!colorData) return;

    // Get the gradient element
    const gradient = document.querySelector(`#gradient-${moduleId}`);
    if (!gradient) return;

    const stopTop = gradient.querySelector('.lb-gradient-stop-top');
    const stopTopEnd = gradient.querySelector('.lb-gradient-stop-top-end');
    const stopBottomStart = gradient.querySelector('.lb-gradient-stop-bottom-start');
    const stopBottom = gradient.querySelector('.lb-gradient-stop-bottom');

    // Remove any existing function label
    const existingLabel = moduleEl.querySelector('.lb-module__function-label');
    if (existingLabel) {
      existingLabel.remove();
    }

    // Get arrows element (for traffic advisors)
    const arrows = moduleEl.querySelector('.lb-module__arrows');

    if (colorData.primary && colorData.secondary) {
      // Show arrows for traffic advisors when a regular dual color is selected
      if (arrows) arrows.style.display = '';
      // Dual color - hard split at 50% (use getModuleFillColor to convert white to off-white)
      let primaryColor = this.getModuleFillColor(colorData.primary);
      let secondaryColor = this.getModuleFillColor(colorData.secondary);

      // Check if module is rotated more than 90° (bottom elements) - swap colors
      // so primary color appears at visual "top" of the rotated module
      const layout = this.configs.layouts[this.state.config.layout];
      const moduleConfig = layout.modules.find(m => m.id === moduleId);
      if (moduleConfig && Math.abs(moduleConfig.rotation || 0) > 90) {
        // Swap colors for inverted modules
        [primaryColor, secondaryColor] = [secondaryColor, primaryColor];
      }

      if (stopTop) stopTop.setAttribute('stop-color', primaryColor);
      if (stopTopEnd) stopTopEnd.setAttribute('stop-color', primaryColor);
      if (stopBottomStart) stopBottomStart.setAttribute('stop-color', secondaryColor);
      if (stopBottom) stopBottom.setAttribute('stop-color', secondaryColor);
    } else if (colorData.description) {
      // Function color (TD, A) - off-white fill with text label (use helper for visibility)
      const fillColor = this.getModuleFillColor(colorData.hex) || '#F2F2F2';
      if (stopTop) stopTop.setAttribute('stop-color', fillColor);
      if (stopTopEnd) stopTopEnd.setAttribute('stop-color', fillColor);
      if (stopBottomStart) stopBottomStart.setAttribute('stop-color', fillColor);
      if (stopBottom) stopBottom.setAttribute('stop-color', fillColor);

      // Hide arrows for traffic advisors when TD or A is selected
      if (arrows) arrows.style.display = 'none';

      // Add function label (TD or A) inside the module
      this.addFunctionLabel(moduleEl, colorData.label);
    } else {
      // Show arrows for traffic advisors when a regular single color is selected
      if (arrows) arrows.style.display = '';
      // Single color - same color for all stops (use getModuleFillColor to convert white to off-white)
      const fillColor = this.getModuleFillColor(colorData.hex) || '#F2F2F2';
      if (stopTop) stopTop.setAttribute('stop-color', fillColor);
      if (stopTopEnd) stopTopEnd.setAttribute('stop-color', fillColor);
      if (stopBottomStart) stopBottomStart.setAttribute('stop-color', fillColor);
      if (stopBottom) stopBottom.setAttribute('stop-color', fillColor);
    }

    // Show the fill and make shape transparent
    if (fillRect) fillRect.style.opacity = '1';
    if (shape) shape.style.fill = 'transparent';
  }

  addFunctionLabel(moduleEl, label) {
    // Get module dimensions from the shape element
    const shape = moduleEl.querySelector('.lb-module__shape');
    if (!shape) return;

    const x = parseFloat(shape.getAttribute('x'));
    const y = parseFloat(shape.getAttribute('y'));
    const width = parseFloat(shape.getAttribute('width'));
    const height = parseFloat(shape.getAttribute('height'));

    // Create text element for the function label
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('class', 'lb-module__function-label');
    text.setAttribute('x', x + width / 2);
    text.setAttribute('y', y + height / 2);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('font-size', '14');
    text.setAttribute('font-weight', '700');
    text.setAttribute('fill', '#011028');
    text.textContent = label;

    // Check for rotation on the shape
    const transform = shape.getAttribute('transform');
    if (transform) {
      text.setAttribute('transform', transform);
    }

    moduleEl.appendChild(text);
  }

  renderNote() {
    const { config } = this.state;

    if (!this.elements.note) return;

    // Combine config.note and config.legend.note if both exist
    const notes = [config.note, config.legend?.note].filter(Boolean);

    if (notes.length > 0) {
      this.elements.note.innerHTML = `<strong>Note:</strong> ${notes.join(' ')}`;
      this.elements.note.style.display = 'block';
    } else {
      this.elements.note.style.display = 'none';
    }
  }

  renderLegend() {
    const { config } = this.state;

    if (!this.elements.legend) return;

    let html = '<h3 class="lb-builder__legend-title">Legend</h3>';
    html += '<div class="lb-builder__legend-content">';

    if (config.legend.lightBar) {
      const items = config.legend.lightBar.split(',').map(item => `<li>${item.trim()}</li>`).join('');
      html += `<div class="lb-builder__legend-group">
        <span class="lb-builder__legend-label">Light Bar:</span>
        <ul class="lb-builder__legend-value">${items}</ul>
      </div>`;
    }
    if (config.legend.modules) {
      const items = config.legend.modules.split(',').map(item => `<li>${item.trim()}</li>`).join('');
      html += `<div class="lb-builder__legend-group">
        <span class="lb-builder__legend-label">Modules:</span>
        <ul class="lb-builder__legend-value">${items}</ul>
      </div>`;
    }
    // Note is now rendered in renderNote() instead

    html += '</div>';

    this.elements.legend.innerHTML = html;
  }

  renderMobileLegend() {
    const { config } = this.state;

    if (!config.legend || (!config.legend.lightBar && !config.legend.modules)) {
      return '';
    }

    let html = '<div class="lb-legend-mobile">';
    html += '<h4 class="lb-legend-mobile__title">Legend</h4>';
    html += '<div class="lb-legend-mobile__content">';

    if (config.legend.lightBar) {
      const items = config.legend.lightBar.split(',').map(item => `<li>${item.trim()}</li>`).join('');
      html += `<div class="lb-legend-mobile__group">
        <span class="lb-legend-mobile__label">Light Bar:</span>
        <ul class="lb-legend-mobile__value">${items}</ul>
      </div>`;
    }
    if (config.legend.modules) {
      const items = config.legend.modules.split(',').map(item => `<li>${item.trim()}</li>`).join('');
      html += `<div class="lb-legend-mobile__group">
        <span class="lb-legend-mobile__label">Modules:</span>
        <ul class="lb-legend-mobile__value">${items}</ul>
      </div>`;
    }

    html += '</div></div>';
    return html;
  }

  renderSidebar() {
    const { currentScreen } = this.state;

    switch (currentScreen) {
      case 'configure':
        this.renderConfigureScreen();
        break;
      case 'accessories':
        this.renderAccessoriesScreen();
        break;
      case 'review':
        this.renderReviewScreen();
        break;
    }
  }

  renderConfigureScreen() {
    const { config, currentStep, moduleColors } = this.state;

    if (!this.elements.sidebarContent) return;

    this.updateSidebarHeader('Build Your Custom Light Bar', 'Select each module color in the steps below.');

    // Check if user has done any configuration (to allow jumping between steps)
    const hasAnyConfiguration = Object.keys(moduleColors).length > 0;

    let html = '';

    config.steps.forEach((step, index) => {
      const stepNum = index + 1;
      const isActive = stepNum === currentStep;
      const isComplete = this.isStepComplete(index);

      // Steps are clickable if:
      // 1. At or before current step (can always go back)
      // 2. Step is complete (can always edit completed steps)
      // 3. Next step after current (if current step is complete)
      // 4. User has started configuring and step has some modules configured
      const currentStepComplete = this.isStepComplete(currentStep - 1);
      const isNextStep = stepNum === currentStep + 1;
      const hasPartialConfig = step.modules.some(m => moduleColors[m]);

      const isClickable = stepNum <= currentStep ||
                          isComplete ||
                          (isNextStep && currentStepComplete) ||
                          hasPartialConfig;

      const progress = this.getStepProgress(index);

      html += `
        <div class="lb-step ${isActive ? 'is-active' : ''} ${!isClickable ? 'is-disabled' : ''}">
          <div class="lb-step__header ${isClickable ? 'is-clickable' : ''}" ${isClickable ? `data-step="${stepNum}"` : ''}>
            <h4 class="lb-step__title">Step ${stepNum}: ${step.title}</h4>
            <span class="lb-step__progress">${progress.current}/${progress.total}</span>
          </div>
          ${isActive ? this.renderColorSwatches(step.colors) : ''}
        </div>
        ${index < config.steps.length - 1 ? '<div class="lb-step__divider"></div>' : ''}
      `;
    });

    // Add mobile legend at the bottom of sidebar content (after all steps)
    html += this.renderMobileLegend();

    this.elements.sidebarContent.innerHTML = html;
    this.updateNextButton();

    // Highlight swatch if selected module already has a color
    this.highlightSelectedSwatch();
  }

  renderColorSwatches(colorSetKey) {
    const colorSet = this.configs.colorSets[colorSetKey];
    if (!colorSet) return '';

    let html = '<div class="lb-step__colors">';

    colorSet.forEach(colorKey => {
      const colorData = this.getColorData(colorKey);
      if (!colorData) return;

      if (colorData.primary && colorData.secondary) {
        // Dual color swatch
        html += `
          <button class="lb-swatch lb-swatch--dual" data-color-key="${colorKey}" title="${colorData.label}">
            <span class="lb-swatch__half lb-swatch__half--top" style="background-color: ${colorData.primary}"></span>
            <span class="lb-swatch__half lb-swatch__half--bottom" style="background-color: ${colorData.secondary}"></span>
          </button>
        `;
      } else if (colorData.description) {
        // Function swatch (TD, A)
        html += `
          <button class="lb-swatch lb-swatch--function" data-color-key="${colorKey}" title="${colorData.description}">
            <span class="lb-swatch__label">${colorData.label}</span>
          </button>
        `;
      } else {
        // Single color swatch
        html += `
          <button class="lb-swatch lb-swatch--single" data-color-key="${colorKey}" title="${colorData.label}">
            <span class="lb-swatch__color" style="background-color: ${colorData.hex}; ${colorData.border ? `border: 1px solid ${colorData.border}` : ''}"></span>
          </button>
        `;
      }
    });

    html += '</div>';
    return html;
  }

  // Return the on-page Rebuy widget element for `rebuyId` that belongs to THIS light bar's
  // product. On the product's own PDP that's the inline widget; when we're editing off-PDP it's
  // the staged widget created by ensureRebuyStaged(). Falls back to any populated widget only
  // when we don't know the product id (legacy behaviour), so we never clone another product's
  // accessories when the drawer is opened on a different product's page.
  findRebuyWidget(rebuyId) {
    const pid = this.state.productId ? String(this.state.productId) : '';
    const nodes = document.querySelectorAll(`[data-rebuy-id="${rebuyId}"]`);
    let anyWithContent = null;
    for (const n of nodes) {
      if (!n.innerHTML.trim()) continue;
      anyWithContent = anyWithContent || n;
      if (!pid) return n;
      const owner = (n.getAttribute('data-rebuy-shopify-product-ids') || n.getAttribute('data-rebuy-product-id') || '').split(',');
      if (owner.indexOf(pid) !== -1) return n;
    }
    return pid ? null : anyWithContent;
  }

  // The accessory/bracket/upgradeable Rebuy widgets only exist in the DOM on the product's own
  // PDP. When "Edit Attributes" is used from the cart page or a drawer opened on another page,
  // they're absent, so the builder used to time out and show the "shown on the product page"
  // fallback. Here we recreate them off-screen for THIS product and let Rebuy render into them,
  // mirroring snippets/edit-accessories-modal.liquid; the existing render loops then clone them.
  ensureRebuyStaged() {
    const pid = this.state.productId ? String(this.state.productId) : '';
    if (!pid) return; // no product context -> fall back to legacy DOM lookup

    // Correct-product widgets already on the page (e.g. we're on its PDP)? Nothing to do.
    const present = ['309200', '309201', '292456', '301541', '290682'].some((id) => this.findRebuyWidget(id));
    if (present) return;

    if (this._rebuyStaged) return; // stage once per builder session
    this._rebuyStaged = true;

    // Give Rebuy this product's context off-PDP. Saved + restored on close() so we never leave
    // another product page's context corrupted.
    this._savedMeta = {
      hadMeta: !!window.meta,
      product: window.meta ? window.meta.product : undefined,
      page: window.meta ? window.meta.page : undefined
    };
    if (!window.meta) window.meta = {};
    window.meta.product = { id: Number(pid) };
    window.meta.page = { pageType: 'product' };
    window.__rebuy_product_id = Number(pid);

    const stage = document.createElement('div');
    stage.className = 'lb-rebuy-stage';
    stage.setAttribute('aria-hidden', 'true');
    // Off-screen but still rendered (Rebuy skips display:none widgets).
    stage.style.cssText = 'position:fixed;left:-99999px;top:0;width:600px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
    stage.innerHTML = ['309200', '309201', '292456', '301541', '290682'].map((id) =>
      `<div data-rebuy-id="${id}" data-rebuy-shopify-product-ids="${pid}" data-rebuy-product-id="${pid}" data-product-id="${pid}"></div>`
    ).join('');
    document.body.appendChild(stage);
    this._rebuyStage = stage;

    // Kick Rebuy to render into the staged containers.
    const tryInit = () => {
      try {
        if (window.Rebuy && typeof window.Rebuy.init === 'function') window.Rebuy.init();
        if (window.Rebuy && window.Rebuy.Widgets && typeof window.Rebuy.Widgets.init === 'function') window.Rebuy.Widgets.init();
      } catch (e) {
        console.warn('[LightBarBuilder] Rebuy init failed:', e);
      }
    };
    setTimeout(tryInit, 50);
  }

  // Have the staged widgets finished rendering? Rebuy always renders a `.rebuy-widget` element
  // per configured widget (visible when it has products, hidden when empty), so once all three
  // are present we can safely clone without racing a still-rendering brackets widget. Always
  // true on the PDP path (nothing staged), so it never delays the existing flow.
  rebuyStageSettled() {
    if (!this._rebuyStage) return true;
    return ['309200', '309201', '292456', '301541', '290682'].every((id) => {
      const el = this._rebuyStage.querySelector(`[data-rebuy-id="${id}"]`);
      return el && el.querySelector('.rebuy-widget');
    });
  }

  // Remove staged widgets + restore the page's original Rebuy product context.
  teardownRebuyStage() {
    if (this._rebuyStage) {
      this._rebuyStage.remove();
      this._rebuyStage = null;
    }
    if (this._savedMeta) {
      if (window.meta) {
        window.meta.product = this._savedMeta.product;
        window.meta.page = this._savedMeta.page;
      }
      this._savedMeta = null;
    }
    this._rebuyStaged = false;
  }

  renderAccessoriesScreen() {
    if (!this.elements.sidebarContent) return;

    // Make sure the Rebuy widgets exist for this product even when we're not on its PDP.
    this.ensureRebuyStaged();

    // Try to find and clone both accessories and brackets widgets (inline on the PDP, or the
    // off-screen staged copies created by ensureRebuyStaged() when editing off-PDP).
    // `force` (last attempt) commits with whatever has rendered even if not fully settled.
    const tryRenderRebuy = (force) => {
      if (!force && !this.rebuyStageSettled()) return false;
      // Check for accessories widget (292456)
      const accessoriesWidget = this.findRebuyWidget('292456');

      // Upgradeable features widget (290682): its own section, independent of accessories (the
      // accessories widget is always present, so an `accessories || upgradeable` fallback hid it).
      const upgradeableWidget = this.findRebuyWidget('290682');

      // Check for brackets widget (301541)
      const bracketsWidget = this.findRebuyWidget('301541');

      // Control Box (309200) + Extension Cables (309201): shown above Accessories, reusing the
      // accessories classes/handlers so they style and add to cart the same way. Only included
      // when the widget actually has products — most parents have none, and the rendered (hidden)
      // widget shell would otherwise leave an empty titled section.
      const controlBoxWidget = this.findRebuyWidget('309200');
      const extensionCablesWidget = this.findRebuyWidget('309201');
      const hasControlBox = controlBoxWidget && controlBoxWidget.querySelector('.rebuy-addon__item');
      const hasExtensionCables = extensionCablesWidget && extensionCablesWidget.querySelector('.rebuy-addon__item');

      const hasAccessories = accessoriesWidget && accessoriesWidget.querySelector('.rebuy-addon__item');
      const hasBrackets = bracketsWidget && bracketsWidget.querySelector('.rebuy-addon__item');
      const hasUpgradeable = upgradeableWidget && upgradeableWidget.querySelector('.rebuy-addon__item');

      if (hasControlBox || hasExtensionCables || hasAccessories || hasBrackets || hasUpgradeable) {
        let html = '<div class="lb-accessories-combined">';

        if (hasControlBox) {
          html += '<div class="lb-accessories lb-accessories--control-box">';
          html += '<div class="lb-accessories__section-title">Switch Box (Recommended)</div>';
          html += `<div class="lb-accessories__rebuy">${controlBoxWidget.innerHTML}</div>`;
          html += '</div>';
        }

        if (hasExtensionCables) {
          html += '<div class="lb-accessories lb-accessories--extension-cables">';
          html += '<div class="lb-accessories__section-title">Extension Cable</div>';
          html += `<div class="lb-accessories__rebuy">${extensionCablesWidget.innerHTML}</div>`;
          html += '</div>';
        }

        // Add accessories section
        if (hasAccessories) {
          html += '<div class="lb-accessories">';
          html += '<div class="lb-accessories__section-title">Accessories</div>';
          html += `<div class="lb-accessories__rebuy">${accessoriesWidget.innerHTML}</div>`;
          html += '</div>';
        }

        // Add brackets section
        if (hasBrackets) {
          html += '<div class="lb-brackets">';
          html += '<div class="lb-brackets__section-title">Mounting Brackets</div>';
          html += `<div class="lb-brackets__rebuy">${bracketsWidget.innerHTML}</div>`;
          html += '</div>';
        }

        // Add upgradeable features section (reuses accessories classes/handlers)
        if (hasUpgradeable) {
          html += '<div class="lb-accessories lb-accessories--upgradeable-features">';
          html += '<div class="lb-accessories__section-title">Upgradeable Features</div>';
          html += `<div class="lb-accessories__rebuy">${upgradeableWidget.innerHTML}</div>`;
          html += '</div>';
        }

        html += '</div>';
        this.elements.sidebarContent.innerHTML = html;
        this.updateSidebarHeader('Choose Accessories & Brackets:');
        this.updateNextButton();

        // Bind events for both sections
        this.bindRebuyEvents();
        this.bindBracketsEvents();
        return true;
      }
      return false;
    };

    // Try immediately
    if (tryRenderRebuy()) return;

    // Show loading state while waiting for Rebuy
    this.elements.sidebarContent.innerHTML = `
      <div class="lb-accessories-combined">
        <div class="lb-accessories__loading">
          <p>Loading accessories & brackets...</p>
        </div>
      </div>
    `;
    this.updateSidebarHeader('Choose Accessories & Brackets:');

    // Retry a few times waiting for Rebuy to load
    let attempts = 0;
    // Allow extra time: off-PDP the widgets are staged and rendered by Rebuy on the fly.
    const maxAttempts = 40;
    const retryInterval = setInterval(() => {
      attempts++;
      const force = attempts >= maxAttempts;
      if (tryRenderRebuy(force)) {
        clearInterval(retryInterval);
        return;
      }
      if (force) {
        clearInterval(retryInterval);
        // Final fallback message
        this.elements.sidebarContent.innerHTML = `
          <div class="lb-accessories-combined">
            <div class="lb-accessories__empty">
              <p>Accessories & brackets will be shown on the product page after configuration.</p>
            </div>
          </div>
        `;
      }
    }, 250);
  }

  renderBracketsScreen() {
    if (!this.elements.sidebarContent) return;

    // Make sure the Rebuy brackets widget exists for this product even when we're not on its PDP.
    this.ensureRebuyStaged();

    // Try to find and clone the Rebuy brackets widget (301541) — inline on the PDP, or the
    // off-screen staged copy created by ensureRebuyStaged() when editing off-PDP.
    // `force` (last attempt) commits with whatever has rendered even if not fully settled.
    const tryRenderRebuy = (force) => {
      if (!force && !this.rebuyStageSettled()) return false;
      const existingWidget = this.findRebuyWidget('301541');

      if (existingWidget && existingWidget.innerHTML.trim()) {
        // Clone the widget content
        let html = '<div class="lb-brackets">';
        html += `<div class="lb-brackets__rebuy">${existingWidget.innerHTML}</div>`;
        html += '</div>';
        this.elements.sidebarContent.innerHTML = html;
        this.updateSidebarHeader('Choose Mounting Brackets:');
        this.updateNextButton();
        this.bindBracketsEvents();
        return true;
      }
      return false;
    };

    // Try immediately
    if (tryRenderRebuy()) return;

    // Show loading state while waiting for Rebuy
    this.elements.sidebarContent.innerHTML = `
      <div class="lb-brackets">
        <div class="lb-brackets__loading">
          <p>Loading brackets...</p>
        </div>
      </div>
    `;
    this.updateSidebarHeader('Choose Mounting Brackets:');

    // Retry a few times waiting for Rebuy to load
    let attempts = 0;
    // Allow extra time: off-PDP the widget is staged and rendered by Rebuy on the fly.
    const maxAttempts = 40;
    const retryInterval = setInterval(() => {
      attempts++;
      const force = attempts >= maxAttempts;
      if (tryRenderRebuy(force)) {
        clearInterval(retryInterval);
        return;
      }
      if (force) {
        clearInterval(retryInterval);
        // Final fallback message
        this.elements.sidebarContent.innerHTML = `
          <div class="lb-brackets">
            <div class="lb-brackets__empty">
              <p>Brackets will be shown on the product page after configuration.</p>
            </div>
          </div>
        `;
      }
    }, 250);
  }

  bindBracketsEvents() {
    // Listen for checkbox changes in the cloned Rebuy brackets widget
    const container = this.elements.sidebarContent.querySelector('.lb-brackets__rebuy');
    if (!container) return;

    // Initialize each item (quantity badges, contextual pricing)
    container.querySelectorAll('.rebuy-addon__item, [data-rebuy-product-id]').forEach(item => {
      this.initializeRebuyItem(item);
    });

    // Bind checkbox change events
    container.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        this.handleBracketsCheckboxChange(e);
      });
    });

    // Apply precheck logic for included brackets
    this.applyRebuyPrecheck(container, 'brackets');
  }

  handleBracketsCheckboxChange(e) {
    const item = e.target.closest('.rebuy-addon__item, [data-rebuy-product-id]');
    if (!item) return;

    const bracketData = this.extractAccessoryData(item);

    if (!bracketData) {
      console.warn('Could not extract bracket data from item');
      return;
    }

    if (e.target.checked) {
      // Add to brackets if not already there
      if (!this.state.brackets.find(b => b.variantId === bracketData.variantId)) {
        this.state.brackets.push(bracketData);
      }
      item.classList.add('is-selected');
    } else {
      // Remove from brackets
      this.state.brackets = this.state.brackets.filter(b => b.variantId !== bracketData.variantId);
      item.classList.remove('is-selected');
    }

    console.log('Current brackets:', this.state.brackets);
  }

  bindRebuyEvents() {
    // Listen for checkbox changes in the cloned Rebuy widgets. Control Box / Extension Cables /
    // Accessories each render their own .lb-accessories__rebuy container, all feeding state.accessories.
    this.elements.sidebarContent.querySelectorAll('.lb-accessories__rebuy').forEach(container => {
      // Initialize each item (quantity badges, contextual pricing)
      container.querySelectorAll('.rebuy-addon__item, [data-rebuy-product-id]').forEach(item => {
        this.initializeRebuyItem(item);
      });

      // Bind checkbox change events
      container.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          this.handleRebuyCheckboxChange(e);
        });
      });

      // Apply precheck logic for included accessories
      this.applyRebuyPrecheck(container, 'accessories');
    });
  }

  /**
   * Initialize a Rebuy item with quantity badges and contextual pricing
   */
  initializeRebuyItem(item) {
    let variantId = item.getAttribute('data-rebuy-variant-id');
    const productId = item.getAttribute('data-rebuy-product-id');

    // Select value takes priority (user may have changed variant in dropdown)
    const select = item.querySelector('select.rebuy-select, select');
    if (select && select.value) {
      variantId = select.value;
    }

    // Fallback: extract variant ID from "Learn More" link URL
    if (!variantId) {
      const link = item.querySelector('a[href*="variant="]');
      if (link) {
        const match = link.href.match(/variant=(\d+)/);
        variantId = match ? match[1] : null;
      }
    }

    // Add quantity indicator if qty > 1 (from STLAddonQty)
    const addonQtyMap = window.STLAddonQty || {};
    let qty = 1;
    if (variantId && addonQtyMap['v' + variantId]) {
      qty = addonQtyMap['v' + variantId];
    } else if (productId && addonQtyMap['p' + productId]) {
      qty = addonQtyMap['p' + productId];
    }

    if (qty > 1) {
      const titleEl = item.querySelector('.rebuy-addon__item-product-title, .rebuy-product-title');
      if (titleEl) {
        // Remove any existing badge first (in case it was cloned from original)
        const existing = titleEl.querySelector('.rebuy-addon-qty');
        if (existing) existing.remove();

        // Add fresh badge with inline styles for visibility
        const qtySpan = document.createElement('span');
        qtySpan.className = 'rebuy-addon-qty';
        qtySpan.style.cssText = 'font-weight: 600; margin-left: 4px;';
        qtySpan.textContent = ' ×' + qty;
        titleEl.appendChild(qtySpan);
      }
    }

    // Apply B2B contextual pricing (from STLContextualPrices)
    this.applyContextualPriceToItem(item, variantId);
  }

  /**
   * Apply B2B contextual pricing to a single item
   */
  applyContextualPriceToItem(item, variantId) {
    const map = window.STLContextualPrices;
    if (!map || !variantId) return;

    const cents = map[variantId];
    if (cents == null) return; // no catalog price for this variant

    const priceEl = item.querySelector('.rebuy-addon__item-product-price .rebuy-money:not(.compare-at), .rebuy-money:not(.compare-at)');
    if (!priceEl) return;

    const desired = this.formatMoneyCents(cents);
    if (priceEl.textContent.trim() === desired) return;
    priceEl.textContent = desired;
  }

  /**
   * Format cents to money string (uses Rebuy's formatter if available)
   */
  formatMoneyCents(cents) {
    if (typeof Rebuy !== 'undefined' && Rebuy.Money && typeof Rebuy.Money.formatMoney === 'function') {
      return Rebuy.Money.formatMoney(cents);
    }
    // Fallback: use locale-aware formatting
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = currency === 'USD' ? 'en-US' : undefined;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  /**
   * Auto-check accessories from STLAddonPrecheck
   */
  applyRebuyPrecheck(container, type = 'accessories') {
    const precheckIds = window.STLAddonPrecheck || [];
    if (precheckIds.length === 0) return;

    const stateArray = type === 'brackets' ? this.state.brackets : this.state.accessories;

    const run = () => {
      const items = container.querySelectorAll('.rebuy-addon__item, [data-rebuy-product-id]');
      items.forEach(item => {
        const checkbox = item.querySelector('.rebuy-checkbox, input[type="checkbox"]');
        if (!checkbox || checkbox.checked) return;

        // Resolve the item's variant/product ids the SAME robust way the add flow does
        // (extractAccessoryData: data attr -> variant select -> product link). The raw
        // data-rebuy-* attributes are present in the PDP's inline widget template but can be
        // absent in the off-PDP (staged) Rebuy template — matching only on them left existing
        // accessories/brackets un-prechecked off-PDP, so saving then dropped them.
        const itemData = this.extractAccessoryData(item);
        if (!itemData) return;

        const vKey = itemData.variantId ? 'v' + itemData.variantId : null;
        const pKey = itemData.productId ? 'p' + itemData.productId : null;
        const matches = (vKey && precheckIds.indexOf(vKey) !== -1) ||
                        (pKey && precheckIds.indexOf(pKey) !== -1);
        if (!matches) return;

        checkbox.checked = true;
        item.classList.add('is-selected');
        if (!stateArray.find(a => a.variantId === itemData.variantId)) {
          stateArray.push(itemData);
        }
      });
      console.log(`Prechecked ${type}:`, stateArray);
    };

    // Run now, then re-run for late/async renders (off-PDP the widget renders on the fly).
    run();
    setTimeout(run, 300);
    setTimeout(run, 800);
  }

  /**
   * Extract accessory data from a Rebuy item element
   */
  extractAccessoryData(item) {
    // Extract variant ID (same logic as rebuy-custom.js and initializeRebuyItem)
    let variantId = item.getAttribute('data-rebuy-variant-id');

    // Select value takes priority (user may have changed variant in dropdown)
    const variantSelect = item.querySelector('select.rebuy-select, select');
    if (variantSelect && variantSelect.value) {
      variantId = variantSelect.value;
    }

    // Fallback: try to get from link
    if (!variantId) {
      const link = item.querySelector('a[href*="variant="]');
      if (link) {
        const match = link.href.match(/variant=(\d+)/);
        variantId = match ? match[1] : null;
      }
    }

    // Final fallback: try other data attributes
    if (!variantId) {
      variantId = item.getAttribute('data-variant-id') ||
                  item.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');
    }

    if (!variantId) return null;

    const priceEl = item.querySelector('.rebuy-money span:not(.sr-only), .rebuy-money, .rebuy-product-price');
    const titleEl = item.querySelector('.rebuy-addon__item-product-title, .rebuy-product-title');

    // Get title without quantity indicator
    let title = titleEl ? titleEl.textContent.trim() : 'Accessory';
    title = title.replace(/\s*×\d+$/, ''); // Remove "×N" suffix if present

    // Get variant title if available
    const variantTitleEl = item.querySelector('.rebuy-addon__item-variant-title, .rebuy-variant-title');
    if (variantTitleEl) {
      const variantTitle = variantTitleEl.textContent.trim();
      if (variantTitle && variantTitle !== 'Default Title' && !title.includes(variantTitle)) {
        title = `${title} - ${variantTitle}`;
      }
    }

    // Get product ID for quantity fallback
    const productId = item.getAttribute('data-rebuy-product-id') ||
                      item.getAttribute('data-product-id') ||
                      item.querySelector('[data-product-id]')?.getAttribute('data-product-id');

    return {
      id: variantId,
      variantId: variantId,
      productId: productId,
      title: title,
      price: priceEl ? this.parseRebuyPrice(priceEl.textContent) : 0
    };
  }

  /**
   * Handle Rebuy checkbox change event
   */
  handleRebuyCheckboxChange(e) {
    const item = e.target.closest('.rebuy-addon__item, [data-rebuy-product-id]');
    if (!item) return;

    const accessoryData = this.extractAccessoryData(item);

    if (!accessoryData) {
      console.warn('Could not extract accessory data from item');
      return;
    }

    if (e.target.checked) {
      // Add to accessories if not already there
      if (!this.state.accessories.find(a => a.variantId === accessoryData.variantId)) {
        this.state.accessories.push(accessoryData);
      }
      item.classList.add('is-selected');
    } else {
      // Remove from accessories
      this.state.accessories = this.state.accessories.filter(a => a.variantId !== accessoryData.variantId);
      item.classList.remove('is-selected');
    }

    console.log('Current accessories:', this.state.accessories);
  }

  parseRebuyPrice(priceString) {
    if (!priceString) return 0;
    const cleaned = priceString.replace(/[^0-9.,]/g, '').replace(',', '');
    return parseFloat(cleaned) || 0;
  }

  renderAccessories() {
    // Rebuy handles its own selection state
  }

  renderReviewScreen() {
    this.renderReview();
    this.updateSidebarHeader('Review Order');
  }

  renderReview() {
    if (!this.elements.sidebarContent) return;

    const { productTitle, basePrice, accessories, brackets, quantity, specialInstructions } = this.state;
    const total = this.calculateTotal();

    let html = '<div class="lb-review">';

    // Order summary section
    html += '<div class="lb-review__summary">';

    // Main product
    html += `
      <div class="lb-review__item">
        <div class="lb-review__item-info">
          <p class="lb-review__item-name">${productTitle}</p>
        </div>
        <span class="lb-review__item-price">${this.formatPrice(basePrice)}</span>
      </div>
    `;

    // Accessories
    accessories.forEach(acc => {
      html += `
        <div class="lb-review__item lb-review__item--accessory">
          <div class="lb-review__item-info">
            <p class="lb-review__item-name">${acc.title}</p>
          </div>
          <span class="lb-review__item-price">${this.formatPrice(acc.price)}</span>
        </div>
      `;
    });

    // Brackets
    brackets.forEach(bracket => {
      html += `
        <div class="lb-review__item lb-review__item--bracket">
          <div class="lb-review__item-info">
            <p class="lb-review__item-name">${bracket.title}</p>
          </div>
          <span class="lb-review__item-price">${this.formatPrice(bracket.price)}</span>
        </div>
      `;
    });

    html += '</div>'; // End summary

    // Special instructions
    html += `
      <div class="lb-review__instructions">
        <label class="lb-review__instructions-label">Special Instructions (Optional)</label>
        <textarea class="lb-review__instructions-input" placeholder="Enter any special instructions...">${specialInstructions}</textarea>
      </div>
    `;

    html += '</div>'; // End review

    this.elements.sidebarContent.innerHTML = html;

    // Update footer with quantity + add to cart
    this.updateReviewButtons(total, quantity);
  }

  updateReviewButtons(total, quantity) {
    const footer = document.querySelector('.lb-sidebar__footer');
    if (!footer) return;

    footer.innerHTML = `
      <div class="lb-review__footer-row">
        <div class="lb-review__quantity-controls">
          <button class="lb-review__quantity-btn" data-action="qty-minus">-</button>
          <span class="lb-review__quantity-value">${quantity}</span>
          <button class="lb-review__quantity-btn" data-action="qty-plus">+</button>
        </div>
        <button class="lb-button lb-button--primary" data-action="add-to-cart">
          Add To Cart - ${this.formatPrice(total)}
        </button>
      </div>
      <div class="lb-button-group">
        <button class="lb-button lb-button--secondary" data-action="back">Back to Edit</button>
        <button class="lb-button lb-button--secondary" data-action="print">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z"/>
          </svg>
          Print
        </button>
      </div>
    `;
  }

  // ========================================
  // Helpers
  // ========================================

  getColorData(colorKey) {
    const { colors } = this.configs;
    return colors.dual[colorKey] || colors.single[colorKey] || colors.functions[colorKey];
  }

  getModuleDisplayName(module) {
    const typeNames = {
      'led': 'LED',
      'alley': 'Alley',
      'takedown': 'Takedown',
      'cruise': 'Cruise',
      'traffic-advisor': 'Traffic Advisor'
    };

    const typeName = typeNames[module.type] || module.type;
    const idNum = module.id.replace(/[^0-9]/g, '');

    if (module.label) {
      return `${module.label} ${module.id.includes('left') ? 'Left' : module.id.includes('right') ? 'Right' : ''}`.trim();
    }

    return `${typeName} ${idNum}`;
  }

  getStepProgress(stepIndex) {
    const { config, moduleColors } = this.state;
    const step = config.steps[stepIndex];
    const total = step.modules.length;
    const current = step.modules.filter(m => moduleColors[m]).length;
    return { current, total };
  }

  updateStepProgress() {
    const { currentStep } = this.state;
    const progress = this.getStepProgress(currentStep - 1);
    const progressEl = document.querySelector('.lb-step.is-active .lb-step__progress');
    if (progressEl) {
      progressEl.textContent = `${progress.current}/${progress.total}`;
    }
  }

  updateNextButton() {
    const { currentStep, currentScreen, config } = this.state;
    const footer = document.querySelector('.lb-sidebar__footer');
    if (!footer) return;

    if (currentScreen === 'configure') {
      const isStepComplete = this.isStepComplete(currentStep - 1);
      const isLastStep = currentStep === config.steps.length;
      const buttonText = isLastStep ? 'Next' : 'Next';

      footer.innerHTML = `
        <button class="lb-button lb-button--primary" data-action="next" ${!isStepComplete ? 'disabled' : ''}>
          ${buttonText}
        </button>
      `;
    } else if (currentScreen === 'accessories') {
      footer.innerHTML = `
        <button class="lb-button lb-button--primary" data-action="next">
          Next
        </button>
      `;
    }
  }

  updateSidebarHeader(title, subtitle = null) {
    const header = document.querySelector('.lb-sidebar__header');
    if (!header) return;

    const titleEl = header.querySelector('.lb-sidebar__title');
    const subtitleEl = header.querySelector('.lb-sidebar__subtitle');

    if (titleEl) titleEl.textContent = title;
    if (subtitleEl) {
      if (subtitle) {
        subtitleEl.textContent = subtitle;
        subtitleEl.style.display = '';
      } else {
        subtitleEl.style.display = 'none';
      }
    }
  }

  highlightSelectedSwatch() {
    const { selectedModule, moduleColors } = this.state;
    const currentColor = moduleColors[selectedModule];

    document.querySelectorAll('.lb-swatch').forEach(el => {
      el.classList.toggle('is-selected', el.dataset.colorKey === currentColor);
    });
  }

  calculateTotal() {
    const { basePrice, accessories, brackets, quantity } = this.state;
    const accessoriesTotal = accessories.reduce((sum, a) => sum + a.price, 0);
    const bracketsTotal = brackets.reduce((sum, b) => sum + b.price, 0);
    return (basePrice + accessoriesTotal + bracketsTotal) * quantity;
  }

  updateTotalPrice() {
    // Update price displays if needed
  }

  formatPrice(amount) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = currency === 'USD' ? 'en-US' : undefined;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }
}

// Initialize
window.lightBarBuilder = new LightBarBuilder();
